import type { CardInfoInterface } from "@/app/[lang]/hotels/components/HotelSearchComponent/HotelSearchDestinationContentComponent/HotelSearchDestinationContentClass";
import { removeUndefinedKeys } from "@/utils/common/convertHelper/convertToObject";
import { getCookies } from "@/utils/common/cookiesHelper/CookiesUtills";
import { apiRequest } from "@/utils/common/serviceApiHelper/serviceApiUtils";
import { checkExistValue } from "@/utils/common/validateHelper/validateUtils";
import { CookiesItemEnum } from "@/utils/enums/CookiesItemEnum";
import usePath from "@/utils/hooks/client/usePath";

import type { PartialCheckinCheckout } from "./hotelServiceInterfaces/getHotelAvailableInterface";
import {
  ResponseHotelAvailablePresenter,
  ResponseHotelPrivileges,
} from "./hotelServiceInterfaces/getHotelAvailableInterface";
import type {
  RequestHotelDetailBySlugInterface,
  RequestHotelDetailInterface,
} from "./hotelServiceInterfaces/getHotelDetailInterface";
import { ResponseHotelGetDetail } from "./hotelServiceInterfaces/getHotelDetailInterface";
import type {
  RequestHotelNearByInterface,
  RequestHotelsNearByInterface,
  RequestListHotelMapInterface,
  ResponseHotelsNearByClass,
} from "./hotelServiceInterfaces/getHotelDetailReDesignInterface";
import {
  ResponseGetHotelDetailBySlugClass,
  ResponseHotelNearByClass,
  ResponseListHotelMap,
} from "./hotelServiceInterfaces/getHotelDetailReDesignInterface";
import type { RequestHotelDisplayNameByLatLongInterface } from "./hotelServiceInterfaces/getHotelDisplayNameByLatLongInterface";
import { ResponseHotelDisplayNameByLatLong } from "./hotelServiceInterfaces/getHotelDisplayNameByLatLongInterface";
import { HotelGreenCertificatePresenter } from "./hotelServiceInterfaces/getHotelGreenCertificate";
import type {
  RequestHotelPolicyBySlugInterface,
  RequestHotelPolicyInterface,
} from "./hotelServiceInterfaces/getHotelPolicyInterface";
import { ResponseHotelPolicy } from "./hotelServiceInterfaces/getHotelPolicyInterface";
import {
  type RequestHotelRecentlyViewInterface,
  ResponseHotelRecentlyView,
} from "./hotelServiceInterfaces/getHotelRecentlyViewInterface";
import type { RequestHotelRoomsAvailableInterface } from "./hotelServiceInterfaces/getHotelRoomsAvailableInterface";
import { NewResponseHotelRoomAvailable } from "./hotelServiceInterfaces/getHotelRoomsAvailableInterface";
import {
  type RequestHotelSeoInterface,
  ResponseHotelSeoClass,
  type ResponseHotelSeoInterface,
} from "./hotelServiceInterfaces/getHotelSeoContent";
import { ResponseTouristInThailand } from "./hotelServiceInterfaces/getTouristInThailandInterface";
import {
  type RequestHotelAllocateInterface,
  ResponseHotelAllocate,
} from "./hotelServiceInterfaces/postHotelAllocateInterface";
import {
  type RequestHotelRegisterInterface,
  ResponseHotelRegister,
} from "./hotelServiceInterfaces/postHotelRegisterInterface";
// import { RequestHotelGreenCertificateInterface } from "./hotelServiceInterfaces/getHotelGreenCertificate";

class HotelServiceApi {
  private baseUrl: string;

  private basePath: string;

  private basePathV2: string;

  private basePathV3: string;

  private basePathV5: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.basePath = "hotel";
    this.basePathV2 = "v2/hotel";
    this.basePathV3 = "v3/hotel";
    this.basePathV5 = "v5/hotel";
  }

  // ******************************************************
  // ****************** Method: GET ***********************
  // ******************************************************
  async getHotelPrivileges(
    params: PartialCheckinCheckout,
  ): Promise<ResponseHotelPrivileges> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV3}/Privileges?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseHotelPrivileges(response);
  }

  async getDestinations() {
    return apiRequest(this.baseUrl, `${this.basePath}/destinations`, "GET");
  }

  async getListHotelMap(params: RequestListHotelMapInterface) {
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV3}/ListHotelMap`,
      "POST",
      params,
    )) as any;

    return response.map((item: any) => new ResponseListHotelMap(item));
  }

  async getHotelGreenCertificate(
    params: string,
  ): Promise<HotelGreenCertificatePresenter | undefined> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV3}/GreenCertificate?${queryParams}`,
      "GET",
    )) as any;

    if (!response.status) {
      return undefined;
    }

    return new HotelGreenCertificatePresenter(response);
  }

  async getHotelRecentlyView(
    params: RequestHotelRecentlyViewInterface,
  ): Promise<ResponseHotelRecentlyView[]> {
    let resultString = params.id
      .map((value, index) => `hotelId[${index}]=${value}`)
      .join("&");
    if (params.checkin && params.checkout) {
      resultString += `&checkin=${params.checkin}&checkout=${params.checkout}`;
    }
    const queryParams = new URLSearchParams(resultString as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV5}/GetHotelRecently?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    )) as any;

    return response.map((r: any) => new ResponseHotelRecentlyView(r)) || [];
  }

  // async getHotelDetail(params: RequestHotelDetailInterface) {
  //   const queryParams = new URLSearchParams(params as any);
  //   const response = await apiRequest(`hotel/detail?${queryParams}`, "GET");

  //   return response as ResponseHotelDetailInterface;
  // }
  async postHotelDetailBySlugNew(params: RequestHotelDetailBySlugInterface) {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/DetailBySlug`,
        "POST",
        params,
      )) as any;
      return new ResponseGetHotelDetailBySlugClass(response);
    } catch (error) {
      return new ResponseGetHotelDetailBySlugClass(undefined);
    }
  }

  async postHotelDetailBySlug(params: RequestHotelDetailBySlugInterface) {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/DetailBySlug`,
        "POST",
        params,
      )) as any;

      return new ResponseHotelGetDetail(response);
    } catch (error) {
      return new ResponseHotelGetDetail(undefined);
    }
  }

  async getHotelDetail(
    params: RequestHotelDetailInterface,
  ): Promise<ResponseHotelGetDetail> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/detail?${queryParams}`,
      "GET",
    )) as any;

    return new ResponseHotelGetDetail(response);
  }

  async getHotelPolicyBySlug(params: RequestHotelPolicyBySlugInterface) {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/hotelPolicy?${queryParams}`,
      "GET",
    )) as any;

    return new ResponseHotelPolicy(response);
  }

  async getHotelPolicy(params: RequestHotelPolicyInterface) {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/hotelPolicy?${queryParams}`,
      "GET",
    )) as any;

    return new ResponseHotelPolicy(response);
  }

  async getHotelNearBy(
    params: RequestHotelNearByInterface,
  ): Promise<ResponseHotelNearByClass[]> {
    const response = (await apiRequest(
      this.baseUrl,
      `v2/hotel/HotelsNearby`,
      "POST",
      params,
    )) as any;
    return response.map((item: any) => new ResponseHotelNearByClass(item));
  }

  async getHotelRoomAvailable(
    params: RequestHotelRoomsAvailableInterface,
    skipCall?: boolean,
  ): Promise<NewResponseHotelRoomAvailable> {
    if (skipCall) return new NewResponseHotelRoomAvailable({});
    const currency = getCookies(CookiesItemEnum.PREFERRED_CURRENCY)
      ? getCookies(CookiesItemEnum.PREFERRED_CURRENCY)!
      : ("THB" as string); // check language from url if not found set default to 'en'

    const requestParamsHotelRoomsAvailable = {
      ...params,
      countryOfPassport: getCookies(CookiesItemEnum.USER_COUNTRY) || "TH",
      preferredCurrency: currency,
    };

    const queryParams = new URLSearchParams(
      requestParamsHotelRoomsAvailable as any,
    );

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV5}/roomsAvailable?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    )) as any;

    if (
      response &&
      checkExistValue(response.status) &&
      response.status === false
    ) {
      throw new Error((response && response.message) || "");
    }
    // return new ResponseHotelRoomAvailable(response);
    return new NewResponseHotelRoomAvailable(response?.data);
  }

  async getTouristInThailand(): Promise<CardInfoInterface[] | null> {
    try {
      const { dynamicImportJsonPath } = usePath();
      const responseJson: CardInfoInterface[] = await dynamicImportJsonPath(
        `${this.basePathV2}/Tourist`,
      );

      if (responseJson && responseJson.length > 0) {
        return new ResponseTouristInThailand(responseJson).destinations;
      }

      // Fallback to API request if responseJson is invalid
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/Tourist`,
        "GET",
      )) as any;

      return new ResponseTouristInThailand(response).destinations;
    } catch (error) {
      // console.error("error", error);
      return null;
    }
  }

  async getPopularTouristInThailand(): Promise<CardInfoInterface[] | null> {
    try {
      const response = null;
      // const response = (await apiRequest(
      //   this.baseUrl,
      //   `${this.basePathV2}/TouristPopular`,
      //   "GET",
      // )) as any; // reset type for add new type
      return response;
    } catch (error) {
      return null;
    }
  }

  async getDisplayNameByLatLong(
    params: RequestHotelDisplayNameByLatLongInterface,
  ): Promise<ResponseHotelDisplayNameByLatLong> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `v2/destination/GetDisplayName?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseHotelDisplayNameByLatLong(response);
  }

  // ******************************************************
  // ****************** Method: POST **********************
  // ******************************************************
  async hotelAllocate(
    params: RequestHotelAllocateInterface,
    logErrorMessage: boolean = false,
  ): Promise<ResponseHotelAllocate> {
    const allocateBaseUrl =
      process.env.NEXT_PUBLIC_ALLOCATE_BASE_URL || this.baseUrl;

    const response = (await apiRequest(
      allocateBaseUrl,
      `${this.basePathV3}/allocate`,
      "POST",
      params,
      logErrorMessage,
    )) as any;

    return new ResponseHotelAllocate(response);
  }

  async getSeoContent(
    params: RequestHotelSeoInterface,
  ): Promise<ResponseHotelSeoInterface> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/Seo?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseHotelSeoClass(response);
  }

  async registerHotel(params: RequestHotelRegisterInterface) {
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/Register`,
      "POST",
      params,
    )) as any;

    return new ResponseHotelRegister(response);
  }

  async getHotelsBySlug(params: {
    countrySlug?: string;
    citySlug?: string;
    areaSlug?: string;
    page: number;
    take: number;
  }): Promise<ResponseHotelAvailablePresenter | undefined> {
    const queryParams = new URLSearchParams(removeUndefinedKeys(params) as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/hotelList?${queryParams}`,
      "GET",
    )) as any;

    if (!response.status || response.total === 0) {
      return undefined;
    }

    return new ResponseHotelAvailablePresenter(response);
  }

  async getHotelsNearBy(
    params: RequestHotelsNearByInterface,
  ): Promise<ResponseHotelsNearByClass> {
    const response = (await apiRequest(
      this.baseUrl,
      `v3/hotel/HotelsNearByGa`,
      "POST",
      params,
    )) as any;

    return response;
  }
}

export default HotelServiceApi;
